/**
 * Inline Report View — reporting experience embedded within the myApp table page.
 *
 * Fetches or creates a report for the current table, then renders the same
 * CustomReportView component used by the standalone reporting module.
 * The user never leaves the myApp context.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ExternalLink, Loader2, Pencil, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PermissionDenied } from "@/components/ui/permission-denied";
import { portalFetch, PortalApiError } from "@/lib/portalApi";
import { useAuth } from "@/contexts/AuthContext";
import { useMyAppPermissions } from "@/hooks/useMyAppPermissions";
import { myappApi } from "@/services/api/myapp";
import { CustomReportView } from "@/components/portal/apps/reporting/custom/CustomReportView";
import { usePortalData } from "@/components/portal/PortalDataProvider";

import type { CustomReportDefinition } from "@/components/portal/apps/reporting/custom/types";

export const Route = createFileRoute("/portal/myapp/$tableId/report")({
  component: TableReportView,
});

function TableReportView() {
  const params = Route.useParams() as { tableId: string };
  const { tableId } = params;
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId ?? "";

  // Permission check — reuse myApp table-level permissions
  const { loading: permsLoading, denied: permissionDenied } =
    useMyAppPermissions(tableId);

  const [reportSlug, setReportSlug] = useState<string | null>(null);
  const [definition, setDefinition] = useState<CustomReportDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasInitialised = useRef(false);

  // Step 1: Get or create the report for this table
  const initReport = useCallback(async () => {
    if (!token || !tableId) return;
    if (hasInitialised.current) return;

    setLoading(true);
    setError(null);

    try {
      // Get or create the report via myApp bridge endpoint
      const result = await myappApi.reporting.getTableReport(token, tableId);
      setReportSlug(result.reportSlug);

      // Fetch the full report definition from the reporting API
      const data = await portalFetch<
        { report?: CustomReportDefinition } & CustomReportDefinition
      >(
        `/api/v1/gateway/reporting/api/v1/reports/${result.reportSlug}`,
        { token },
      );
      setDefinition(data.report ?? data);
      hasInitialised.current = true;
    } catch (err) {
      console.error("Failed to initialise table report:", err);
      if (err instanceof PortalApiError && err.isPermissionDenied) {
        setError("You don't have permission to view this report.");
      } else {
        setError(
          err instanceof Error ? err.message : "Failed to load report",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [token, tableId]);

  useEffect(() => {
    if (!permsLoading && !permissionDenied) {
      initReport();
    }
  }, [permsLoading, permissionDenied, initReport]);

  // --- Render ---

  if (permsLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-500">Checking permissions...</span>
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <PermissionDenied
        message="You don't have permission to view this table."
        backTo="/portal/myapp"
        backLabel="Back to Tables"
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-500">Preparing report...</span>
      </div>
    );
  }

  if (error || !definition) {
    return (
      <div className="space-y-4">
        <Link
          to="/portal/myapp/$tableId"
          params={{ tableId }}
          className="flex items-center gap-2 text-sm text-primary transition hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Table
        </Link>
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-center">
          <p className="text-sm text-destructive">
            {error || "Failed to load report"}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => { hasInitialised.current = false; initReport(); }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/portal/myapp/$tableId"
            params={{ tableId }}
            className="flex items-center gap-2 text-sm text-primary transition hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Table
          </Link>
          <h1 className="mt-4 text-2xl font-bold text-slate-900">
            {definition.name}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Button asChild variant="outline">
            <Link
              to={"/portal/apps/reporting/custom/$slug/edit" as string}
              params={{ slug: reportSlug! }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit Report
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link
              to={"/portal/apps/reporting/custom/$slug" as string}
              params={{ slug: reportSlug! }}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Open in Reports
            </Link>
          </Button>
        </div>
      </div>

      {/* Report content — reuse the existing reporting component */}
      <CustomReportView
        definition={definition}
        organisationId={organisationId}
        // A MyApp table can be any size — page it in SQL (dp #2214) when the
        // report is a flat grid (CustomReportView checks the definition).
        serverPaged
      />
    </div>
  );
}
